<template>
  <v-card
    class="outlined-card pa-5 d-flex flex-column"
    elevation="0"
    style="height: 100%;"
  >
    <div class="d-flex justify-space-between align-center mb-4">
      <h3 class="text-subtitle-1 font-weight-bold text-uppercase" style="color: #202A63;">Phản hồi phụ huynh</h3>
      <div v-if="notCompletedCount > 0" class="text-body-2" style="color: #FF5252;">
        {{ notCompletedCount }} chưa xử lý
      </div>
    </div>

    <div class="flex-grow-1">
      <div v-if="loading" class="text-center py-4">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
      <div v-else-if="!tickets.length" class="text-center py-4 grey--text">
        Chưa có phản hồi nào
      </div>
      <template v-else>
        <div v-for="(ticket, index) in tickets" :key="ticket.id">
          <div class="mb-4">
            <div class="d-flex justify-space-between align-center mb-1">
              <div class="font-weight-medium text-body-2" style="color: #202A63; font-size: 14px;">
                {{ getSenderName(ticket) }} <v-icon small color="grey lighten-1" class="mx-1">mdi-menu-right</v-icon>
                <span class="font-weight-bold" style="color: #202A63;">BGH Nhà Trường</span>
              </div>
              <router-link :to="`/student-tickets/${ticket.id}`" class="text-body-2 blue--text font-weight-medium text-decoration-none">Xem chi tiết</router-link>
            </div>
            <div class="text-caption grey--text mb-2">{{ formatDate(ticket.createdAt) }}</div>
            <div class="text-body-2 mb-3" style="color: #202A63;">{{ getPreview(ticket.title || ticket.content) }}</div>
            <v-chip v-if="ticket.status === 'completed'" color="green lighten-5" text-color="green darken-2" class="font-weight-bold px-2 py-1" style="font-size: 14px;">
              <v-icon small left color="green">mdi-check</v-icon> Đã xử lý
            </v-chip>
            <v-chip v-else color="orange lighten-5" text-color="orange darken-2" class="font-weight-bold px-2 py-1" style="font-size: 14px;">
              <v-icon small left color="orange">mdi-alert-circle-outline</v-icon> Chưa xử lý
            </v-chip>
          </div>
          <v-divider v-if="index < tickets.length - 1" class="mb-4"></v-divider>
        </div>
      </template>
    </div>

    <!-- Pagination -->
    <div class="d-flex justify-center mt-4" v-if="totalPages > 1">
      <div class="d-flex align-center text-caption font-weight-medium" style="gap: 12px; color: #757575;">
        <v-icon small :color="currentPage > 1 ? 'blue' : 'grey'" class="page-nav" @click="goToPage(currentPage - 1)">mdi-chevron-left</v-icon>
        <span
          v-for="(p, i) in displayedPages"
          :key="i"
          :class="p === '...' ? 'page-ellipsis' : ['page-item', { active: p === currentPage }]"
          @click="p !== '...' && goToPage(p)"
        >{{ p }}</span>
        <v-icon small :color="currentPage < totalPages ? 'blue' : 'grey'" class="page-nav" @click="goToPage(currentPage + 1)">mdi-chevron-right</v-icon>
      </div>
    </div>
  </v-card>
</template>

<script>
import { StudentTicket } from '@/plugins/api'
import moment from 'moment'

const PAGE_SIZE = 5

export default {
  name: 'DashboardFeedbacks',
  data() {
    return {
      tickets: [],
      notCompletedCount: 0,
      loading: false,
      currentPage: 1,
      totalCount: 0,
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / PAGE_SIZE)
    },
    displayedPages() {
      const total = this.totalPages
      const cur = this.currentPage
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
      const pages = []
      if (cur <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      } else if (cur >= total - 3) {
        pages.push(1)
        pages.push('...')
        for (let i = total - 4; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = cur - 1; i <= cur + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      }
      return pages
    }
  },
  async created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const [tickets, totalCount, notCompleted] = await Promise.all([
          StudentTicket.search({
            _limit: PAGE_SIZE,
            _start: (this.currentPage - 1) * PAGE_SIZE,
            _sort: 'createdAt:DESC'
          }).catch(() => []),
          StudentTicket.count({}).catch(() => 0),
          StudentTicket.count({ status_ne: 'completed' }).catch(() => 0),
        ])

        this.tickets = tickets || []
        this.totalCount = typeof totalCount === 'number' ? totalCount : Number(totalCount || 0)
        this.notCompletedCount = typeof notCompleted === 'number' ? notCompleted : Number(notCompleted || 0)
      } catch (error) {
        console.error('Error fetching tickets:', error)
      } finally {
        this.loading = false
      }
    },
    async goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) return
      this.currentPage = page
      await this.fetchData()
    },
    formatDate(date) {
      if (!date) return ''
      return moment(date).format('HH:mm, DD/MM/YYYY')
    },
    getSenderName(ticket) {
      if (ticket.student) {
        return `PH em ${ticket.student.name || 'Học sinh'}`
      }
      return 'Phụ huynh'
    },
    getPreview(value) {
      if (!value) return ''
      return value.substring(0, 100) + (value.length > 100 ? '...' : '')
    }
  }
}
</script>

<style scoped>
.outlined-card {
  border: 1px solid #dde3f0 !important;
  border-radius: 16px !important;
  box-shadow: 0 2px 12px rgba(25, 50, 120, 0.08) !important;
}

.page-item {
  padding: 2px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
}

.page-item:hover {
  background-color: #2196F3;
  color: white !important;
}

.page-item.active {
  border: 1px solid #2196F3;
  color: #2196F3;
}

.page-nav {
  cursor: pointer;
}
.page-nav:hover {
  color: #2196F3 !important;
}

.page-ellipsis {
  color: #9e9e9e;
  padding: 2px 4px;
}
</style>
